// Phone number handling for the shopping iframe.

export const sanitizePhoneNumber = phoneNumber =>
  String(phoneNumber || '')
    .replace(/[^\d]/g, '')
    .trim();

// E.164 allows at most 15 digits. The lower bound is deliberately loose --
// some country codes have short subscriber numbers -- and it only ever
// applies to the fallback path below, where we have nothing to compare to.
const E164_MIN_LENGTH = 8;
const E164_MAX_LENGTH = 15;

/**
 * Decide which number to WhatsApp.
 *
 * Two different numbers get conflated in this app:
 *
 * @param {string} candidatePhoneNumber the number the UI was about to send
 * @param {string} chatwootPhoneNumber  the conversation's contact number (E.164)
 * @returns {{phoneNumber: string|null, error: string|null, corrected: boolean}}
 */
export const resolveMessagingPhoneNumber = (
  candidatePhoneNumber,
  chatwootPhoneNumber
) => {
  const candidate = sanitizePhoneNumber(candidatePhoneNumber);
  const chatwootDigits = sanitizePhoneNumber(chatwootPhoneNumber);

  // Chatwoot knows the contact: that is the WhatsApp identity, full stop.
  if (chatwootDigits) {
    return {
      phoneNumber: chatwootDigits,
      error: null,
      corrected: Boolean(candidate) && candidate !== chatwootDigits,
    };
  }

  // No contact number from Chatwoot (Instagram-only conversations, or a
  // postMessage payload without a contact). Fall back to whatever the UI had,
  // with a length sanity check since there is nothing to compare against.
  if (!candidate) {
    return {
      phoneNumber: null,
      error: 'No WhatsApp number found for this conversation.',
      corrected: false,
    };
  }

  if (
    candidate.length < E164_MIN_LENGTH ||
    candidate.length > E164_MAX_LENGTH
  ) {
    return {
      phoneNumber: null,
      error: `Invalid WhatsApp number: ${candidate}. Expected a full number with country code.`,
      corrected: false,
    };
  }

  return {phoneNumber: candidate, error: null, corrected: false};
};
